import React, { useMemo, useState, useEffect, useCallback } from 'react';
import { noop, isNil, curryRight, constant } from 'lodash-es';
import styled from '../../../../styled';
import dayjs, { getLocaleKey } from '../day';
import CalendarHeader from './CalendarHeader';
import DateCalendarView from './DateCalendarView';
import MonthCalendarView from './MonthCalendarView';
import YearCalendarView from './YearCalendarView';
import { MODE_MONTH, MODE_DATE, MODE_YEAR } from '../constants';
export const Grid = styled.div `
  width: 15.75rem;
`;
const RangeGridContainer = styled.div `
  display: flex;
  cursor: default;

  > ${Grid} {
    &:not(:first-of-type) {
      margin-left: 1rem;
    }

    &:not(:last-of-type) {
      margin-right: 1rem;
    }
  }
`;
export const updateTargetDateInDoubleMonthMode = curryRight((prevTargetDate, nextTargetDate) => nextTargetDate.isBetween(prevTargetDate.startOf('month'), prevTargetDate.add(1, 'month').endOf('month'), 'day', '[]')
    ? prevTargetDate
    : nextTargetDate, 2);
const MultiRangeCalendar = ({ lang = 'en', open, ranges, focusedInput, onChange = noop, disabledDate = () => false, getDateColor, singleMonthMode = false, e2ePrevPageButtonId, e2eNextPageButtonId, }) => {
    var _a, _b, _c, _d;
    const [mode, setMode] = useState(MODE_DATE);
    const [targetDate, setTargetDate] = useState(() => ((ranges === null || ranges === void 0 ? void 0 : ranges[0].from) ||
        (ranges === null || ranges === void 0 ? void 0 : ranges[0].to) ||
        dayjs(lang).set('minute', 0).set('second', 0)).locale(getLocaleKey(lang)));
    const onPageChanged = useCallback((diff) => () => {
        switch (mode) {
            case MODE_DATE:
                setTargetDate(prev => prev.add(diff, 'month'));
                break;
            case MODE_MONTH:
                setTargetDate(prev => prev.add(diff, 'year'));
                break;
            case MODE_YEAR:
                setTargetDate(prev => prev.add(diff * 10, 'year'));
                break;
            default:
                break;
        }
    }, [mode]);
    const onMonthChanged = (m) => {
        setTargetDate(targetDate.month(m));
        setMode(MODE_DATE);
    };
    const onYearChanged = (y) => {
        setTargetDate(targetDate.year(y));
        setMode(MODE_MONTH);
    };
    const getIsDisabled = useCallback((d) => disabledDate(d, mode), [mode, disabledDate]);
    const focusedDate = useMemo(() => {
        var _a, _b;
        if (!isNil(focusedInput)) {
            const { rangeIndex, anchor } = focusedInput;
            return (_b = (_a = ranges === null || ranges === void 0 ? void 0 : ranges[rangeIndex]) === null || _a === void 0 ? void 0 : _a[anchor]) !== null && _b !== void 0 ? _b : null;
        }
        return null;
    }, [focusedInput, ranges]);
    const targetEndDate = useMemo(() => (singleMonthMode ? targetDate : targetDate.add(1, 'month')), [targetDate, singleMonthMode]);
    const checkFocusedDateInTargetDateRange = singleMonthMode
        ? constant
        : updateTargetDateInDoubleMonthMode;
    useEffect(() => {
        if (focusedDate) {
            setTargetDate(checkFocusedDateInTargetDateRange(focusedDate));
        }
    }, [open, focusedDate, checkFocusedDateInTargetDateRange]);
    if (mode === MODE_DATE) {
        return (React.createElement(RangeGridContainer, null,
            React.createElement(Grid, null,
                React.createElement(CalendarHeader, { lang: lang, mode: mode, target: targetDate, onPrevPage: onPageChanged(-1), onNextPage: onPageChanged(1), onChangeMode: setMode, hideNextPage: !singleMonthMode, e2ePrevPageButtonId: e2ePrevPageButtonId, e2eNextPageButtonId: e2eNextPageButtonId }),
                React.createElement(DateCalendarView, { lang: lang, target: targetDate, ranges: ranges, onSelect: onChange, disabledDate: getIsDisabled, getDateColor: getDateColor })),
            !singleMonthMode && (React.createElement(Grid, null,
                React.createElement(CalendarHeader, { lang: lang, mode: mode, target: targetEndDate, onPrevPage: onPageChanged(-1), onNextPage: onPageChanged(1), onChangeMode: setMode, hidePrevPage: true, e2ePrevPageButtonId: e2ePrevPageButtonId, e2eNextPageButtonId: e2eNextPageButtonId }),
                React.createElement(DateCalendarView, { lang: lang, target: targetEndDate, ranges: ranges, onSelect: onChange, disabledDate: getIsDisabled, getDateColor: getDateColor })))));
    }
    return (React.createElement(Grid, null,
        React.createElement(CalendarHeader, { lang: lang, mode: mode, target: targetDate, onPrevPage: onPageChanged(-1), onNextPage: onPageChanged(1), onChangeMode: setMode, e2ePrevPageButtonId: e2ePrevPageButtonId, e2eNextPageButtonId: e2eNextPageButtonId }),
        mode === MODE_MONTH && (React.createElement(MonthCalendarView, { lang: lang, target: targetDate, selectedFrom: (_a = ranges === null || ranges === void 0 ? void 0 : ranges[0]) === null || _a === void 0 ? void 0 : _a.from, selectedTo: (_b = ranges === null || ranges === void 0 ? void 0 : ranges[0]) === null || _b === void 0 ? void 0 : _b.to, onSelect: onMonthChanged, disabledDate: getIsDisabled })),
        mode === MODE_YEAR && (React.createElement(YearCalendarView, { target: targetDate, selectedFrom: (_c = ranges === null || ranges === void 0 ? void 0 : ranges[0]) === null || _c === void 0 ? void 0 : _c.from, selectedTo: (_d = ranges === null || ranges === void 0 ? void 0 : ranges[0]) === null || _d === void 0 ? void 0 : _d.to, onSelect: onYearChanged, disabledDate: getIsDisabled }))));
};
export default MultiRangeCalendar;
//# sourceMappingURL=MultiRangeCalendar.js.map